<script setup lang="ts">
import BulkRowPlace from './BulkRowPlace.vue';
import { computed, ref, watch } from 'vue';
import { useQuery, keepPreviousData } from '@tanstack/vue-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  fetchBulkRunItems,
  type BulkItemOutcome,
  type BulkRun,
  type BulkRunItem,
} from '@/composables/api/useBulkActionApi';
import { bulkRunDoneSummary } from '@/composables/useBulkActionRun';

/**
 * The report of a finished bulk run: what changed and what failed, a page at a time.
 *
 * The counts come from the run. The rows come from `GET /api/bulk-action-runs/{run}/items`, in the
 * order the search page showed them. The labels were captured at the preview, so a deleted row
 * still shows its list and zone.
 *
 * An action on lists and schedules is two runs: the schedules first, then the lists. The bar
 * passes the schedule run as `previousRun`. The report then has a section for each run, and the
 * counts and rows below are those of the section chosen. It opens on the latest run.
 */
const props = withDefaults(defineProps<{
  run: BulkRun;
  previousRun?: BulkRun | null;
}>(), { previousRun: null });

const open = defineModel<boolean>('open', { default: false });

const PER_PAGE = 50;

/** Every outcome that means the row was not changed as asked. */
const FAILED_OUTCOMES: BulkItemOutcome[] = [
  'no_value',
  'invalid',
  'changed_since_confirmed',
  'not_found',
  'not_allowed',
  'error',
  'not_attempted',
];

type ReportView = 'failed' | 'changed' | 'all';

const OUTCOME_LABELS: Record<BulkItemOutcome, string> = {
  done: 'Changed',
  unchanged: 'Already matched',
  no_value: 'No value',
  invalid: "Can't be changed",
  changed_since_confirmed: 'Edited after you confirmed',
  not_found: 'No longer exists',
  not_allowed: 'No access',
  error: 'Error',
  not_attempted: 'Not reached',
};

/** The runs of this action, oldest first. One unless the action ran on lists and schedules. */
const runs = computed<BulkRun[]>(() => (props.previousRun ? [props.previousRun, props.run] : [props.run]));
const shownRunId = ref(props.run.id);
/** The run whose counts and rows are shown. */
const shownRun = computed(() => runs.value.find((candidate) => candidate.id === shownRunId.value) ?? props.run);

function runHeading(candidate: BulkRun): string {
  return candidate.entity === 'location_zone_custom_playlist' ? 'Custom lists' : 'Schedules';
}

function outcomeLabel(outcome: BulkItemOutcome | null): string {
  if (outcome === null) return 'Not run';
  if (outcome === 'done') {
    switch (shownRun.value.action) {
      case 'delete':
        return 'Deleted';
      case 'add_tag':
        return 'Tag added';
      case 'remove_tag':
        return 'Tag removed';
      default:
        return 'Changed';
    }
  }
  return OUTCOME_LABELS[outcome];
}

function failedCountOf(candidate: BulkRun): number {
  return FAILED_OUTCOMES.reduce((sum, outcome) => sum + (candidate.outcomes?.[outcome] ?? 0), 0);
}

const failedCount = computed(() => failedCountOf(shownRun.value));
const changedCount = computed(() => shownRun.value.outcomes?.done ?? 0);

// Open on the failures when there are any. They are what the user has to act on.
const view = ref<ReportView>(failedCount.value > 0 ? 'failed' : 'changed');
const page = ref(1);

watch(view, () => {
  page.value = 1;
});
watch(
  () => props.run.id,
  (runId) => {
    shownRunId.value = runId;
  },
);
watch(shownRunId, () => {
  view.value = failedCount.value > 0 ? 'failed' : 'changed';
  page.value = 1;
});

const outcomeFilter = computed<BulkItemOutcome[] | undefined>(() => {
  if (view.value === 'failed') return FAILED_OUTCOMES;
  if (view.value === 'changed') return ['done'];
  return undefined;
});

const itemsQuery = useQuery({
  queryKey: computed(() => ['bulk-action-run-items', shownRun.value.id, view.value, page.value] as const),
  queryFn: () => fetchBulkRunItems(shownRun.value.id, { page: page.value, per_page: PER_PAGE, outcome: outcomeFilter.value }),
  enabled: open,
  placeholderData: keepPreviousData,
  retry: 0,
});

const items = computed<BulkRunItem[]>(() => itemsQuery.data.value?.data ?? []);
/**
 * True while another tab or page loads. The last rows stay on screen meanwhile, so the list does
 * not jump, but they are faded: they belong to the tab or page just left, not the one chosen.
 */
const showsPreviousRows = computed(() => itemsQuery.isPlaceholderData.value);
const lastPage = computed(() => itemsQuery.data.value?.meta?.last_page ?? 1);

const outcomeCounts = computed(() =>
  (Object.entries(shownRun.value.outcomes ?? {}) as [BulkItemOutcome, number][])
    .filter(([, count]) => count > 0)
    .map(([outcome, count]) => ({ outcome, label: outcomeLabel(outcome), count })),
);


/**
 * The line under the title, built here rather than in the template. The template compiler drops a
 * space at the start of an element's text, so the two sentences used to run together.
 */
function summaryOf(candidate: BulkRun): string {
  const summary = candidate.status === 'failed' ? 'The run stopped before it finished.' : bulkRunDoneSummary(candidate);
  const failed = failedCountOf(candidate);
  return failed > 0 ? `${summary} ${failed.toLocaleString()} could not be changed.` : summary;
}

const summaryText = computed(() => runs.value.map(summaryOf).join(' '));

function itemDetail(item: BulkRunItem): string | null {
  if (item.message) return item.message;
  if (item.no_value_fields.length > 0) return `No value in ${item.no_value_fields.join(', ')}.`;
  return null;
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-2xl" data-testid="bulk-run-report">
      <DialogHeader>
        <DialogTitle>Bulk action report</DialogTitle>
        <DialogDescription data-testid="bulk-run-report-summary">{{ summaryText }}</DialogDescription>
      </DialogHeader>

      <div v-if="runs.length > 1" class="flex gap-1" role="tablist" data-testid="bulk-run-report-sections">
        <Button
          v-for="candidate in runs"
          :key="candidate.id"
          size="sm"
          :variant="candidate.id === shownRun.id ? 'default' : 'outline'"
          role="tab"
          :aria-selected="candidate.id === shownRun.id"
          :data-testid="`bulk-run-report-section-${candidate.id}`"
          @click="shownRunId = candidate.id"
        >
          {{ runHeading(candidate) }}
        </Button>
      </div>

      <ul class="flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid="bulk-run-report-counts">
        <!-- The space between the two spans is its own text node. A space inside a span, at the start
             of its text, would be dropped by the template compiler. -->
        <li v-for="entry in outcomeCounts" :key="entry.outcome">
          <span class="font-medium">{{ entry.count.toLocaleString() }}</span>{{ ' ' }}<span class="text-muted-foreground">{{ entry.label.toLowerCase() }}</span>
        </li>
        <li v-if="shownRun.counts.skipped_for_access > 0" class="text-muted-foreground">
          {{ shownRun.counts.skipped_for_access.toLocaleString() }} skipped because you don't have access
        </li>
      </ul>

      <div class="flex gap-1" role="tablist">
        <Button
          size="sm"
          :variant="view === 'failed' ? 'default' : 'outline'"
          role="tab"
          :aria-selected="view === 'failed'"
          data-testid="bulk-run-report-view-failed"
          @click="view = 'failed'"
        >
          Not changed ({{ failedCount.toLocaleString() }})
        </Button>
        <Button
          size="sm"
          :variant="view === 'changed' ? 'default' : 'outline'"
          role="tab"
          :aria-selected="view === 'changed'"
          data-testid="bulk-run-report-view-changed"
          @click="view = 'changed'"
        >
          {{ outcomeLabel('done') }} ({{ changedCount.toLocaleString() }})
        </Button>
        <Button
          size="sm"
          :variant="view === 'all' ? 'default' : 'outline'"
          role="tab"
          :aria-selected="view === 'all'"
          data-testid="bulk-run-report-view-all"
          @click="view = 'all'"
        >
          All
        </Button>
      </div>

      <div class="max-h-[50vh] overflow-auto">
        <p v-if="itemsQuery.isLoading.value" class="text-muted-foreground text-sm">Loading…</p>
        <p v-else-if="itemsQuery.isError.value" class="text-destructive text-sm">Couldn't load the report. Try again.</p>
        <p v-else-if="items.length === 0" class="text-muted-foreground text-sm">No rows here.</p>
        <ul
          v-else
          class="divide-border/60 divide-y rounded-md border text-sm transition-opacity"
          :class="showsPreviousRows ? 'opacity-40' : ''"
          :aria-busy="showsPreviousRows ? 'true' : undefined"
          data-testid="bulk-run-report-items"
        >
          <li
            v-for="item in items"
            :key="item.id"
            class="flex items-start justify-between gap-3 px-2 py-1.5"
            :data-testid="`bulk-run-report-item-${item.record_id}`"
          >
            <div class="min-w-0">
              <BulkRowPlace :item="item" />
              <p v-if="itemDetail(item)" class="text-muted-foreground">{{ itemDetail(item) }}</p>
            </div>
            <span class="text-muted-foreground shrink-0 text-xs">{{ outcomeLabel(item.outcome) }}</span>
          </li>
        </ul>
      </div>

      <div v-if="lastPage > 1" class="flex items-center justify-end gap-2 text-sm">
        <Button size="sm" variant="outline" :disabled="page <= 1" data-testid="bulk-run-report-prev" @click="page -= 1">
          Previous
        </Button>
        <span class="text-muted-foreground">Page {{ page }} of {{ lastPage }}</span>
        <Button size="sm" variant="outline" :disabled="page >= lastPage" data-testid="bulk-run-report-next" @click="page += 1">
          Next
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
